import { useCallback, useEffect, useState } from 'react';

import CallTaskModal from './CallTaskModal';
import CallTaskTable from './CallTaskTable';
import { Frame, FilterLabel, Notice, Segment, Select, Toolbar } from './styles';
import useCallTasks, { CALL_TASKS_PER_PAGE } from './useCallTasks';
import { JOURNEY_NAMES } from '../shared/labels';
import SectionState from '../shared/SectionState';
import { useLifecycle } from '../LifecycleContext';
import { CallTasksParams, useLifecycleUrlState } from '../useLifecycleUrlState';

import Button from '@/components/Button';
import { Small } from '@/components/Typography';
import useAuth from '@/hooks/useAuth';
import { trackLifecycleFeatureUsed } from '@/util/vexo';

const JOURNEY_OPTIONS = ['J1', 'J2', 'J5', 'J6'].map((key) => ({
  key,
  label: `${key} · ${JOURNEY_NAMES[key]}`,
}));

const STATUS_OPTIONS: { value: CallTasksParams['status']; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'skipped', label: 'Closed' },
];

const numericUserId = (id: unknown): number | null => {
  const value = Number(id);
  return Number.isInteger(value) && value > 0 ? value : null;
};

const CallTasksTab = () => {
  const { params, update } = useLifecycleUrlState<'call-tasks'>();
  const { currentAdmin } = useAuth();
  const myUserId = numericUserId(currentAdmin?.user?.id);
  const mine = params.mine && myUserId !== null;
  const { tasks, pagination, loading, error, reload, retry } = useCallTasks(
    { ...params, mine },
    myUserId,
  );
  const { refreshOpenCount } = useLifecycle();
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  const openTask = useCallback(
    (id: number) => {
      const opened = tasks.find((task) => task.id === id);
      trackLifecycleFeatureUsed({
        feature: 'call_task_open',
        surface: 'call_tasks',
        journey_key: opened?.journey_key ?? null,
        step_key: opened?.step_key ?? null,
        task_id: id,
      });
      update({ task: id }, { replace: true });
    },
    [tasks, update],
  );
  const closeTask = useCallback(
    () => update({ task: null }, { replace: true }),
    [update],
  );
  const changed = useCallback(() => {
    reload();
    refreshOpenCount();
  }, [reload, refreshOpenCount]);
  const completed = useCallback(
    (text: string) => {
      setMessage(text);
      closeTask();
    },
    [closeTask],
  );

  const totalPages = Math.max(pagination?.total_pages ?? 1, 1);
  const total = pagination?.total ?? tasks.length;

  const renderBody = () => {
    if (loading) return <SectionState mode="loading" title="call tasks" />;
    if (error) {
      return (
        <SectionState
          mode="error"
          error={error}
          title="call tasks"
          onRetry={retry}
        />
      );
    }
    if (tasks.length === 0 && params.page > 1) {
      return (
        <>
          <SectionState mode="empty" title="No calls on this page." />
          <div style={{ padding: '0 20px 20px' }}>
            <Button
              a11yLabel="Go to page 1"
              label="Go to page 1"
              appearance="outline"
              size="sm"
              onClick={() => update({ page: 1 })}
            />
          </div>
        </>
      );
    }
    if (tasks.length === 0) {
      return (
        <SectionState
          mode="empty"
          title="No calls due"
          caption="New tasks appear here when a worker stays stuck on a journey step."
        />
      );
    }
    return (
      <>
        <CallTaskTable tasks={tasks} onOpen={openTask} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            borderTop: '1px solid #EDEDED',
          }}
        >
          <Small color="light">{`${total} ${total === 1 ? 'task' : 'tasks'}`}</Small>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              a11yLabel="Previous page"
              label="Previous"
              appearance="outline"
              size="sm"
              disabled={params.page <= 1}
              onClick={() => update({ page: params.page - 1 })}
            />
            <Small color="light">{`Page ${params.page} of ${totalPages} · ${CALL_TASKS_PER_PAGE} per page`}</Small>
            <Button
              a11yLabel="Next page"
              label="Next"
              appearance="outline"
              size="sm"
              disabled={params.page >= totalPages}
              onClick={() => update({ page: params.page + 1 })}
            />
          </span>
        </div>
      </>
    );
  };

  return (
    <div>
      <Toolbar>
        <Segment
          role="group"
          aria-label="Whose tasks"
          data-testid="callTaskMineToggle"
        >
          <button
            type="button"
            aria-pressed={!mine}
            onClick={() => update({ mine: false }, { replace: true })}
          >
            {`All ${(
              STATUS_OPTIONS.find(({ value }) => value === params.status)
                ?.label ?? 'open'
            ).toLowerCase()}`}
          </button>
          <button
            type="button"
            aria-pressed={mine}
            disabled={myUserId === null}
            onClick={() => update({ mine: true }, { replace: true })}
          >
            My tasks
          </button>
        </Segment>
        <FilterLabel>
          Journey
          <Select
            data-testid="callTaskJourneyFilter"
            value={params.journey ?? ''}
            onChange={(e) =>
              update(
                {
                  journey: (e.target.value ||
                    null) as CallTasksParams['journey'],
                },
                { replace: true },
              )
            }
          >
            <option value="">All journeys</option>
            {JOURNEY_OPTIONS.map(({ key, label }) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        </FilterLabel>
        <FilterLabel>
          Status
          <Select
            data-testid="callTaskStatusFilter"
            value={params.status}
            onChange={(e) =>
              update(
                { status: e.target.value as CallTasksParams['status'] },
                { replace: true },
              )
            }
          >
            {STATUS_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FilterLabel>
        <Small color="light" css={{ marginLeft: 'auto' }}>
          Calls are created by journeys J1, J2, J5 and J6 when the worker is
          still stuck.
        </Small>
      </Toolbar>
      {message && (
        <div role="status" style={{ marginBottom: 12 }}>
          <Notice tone="green">{message}</Notice>
        </div>
      )}
      <Frame data-testid="callTaskTable" aria-label="Call tasks">
        {renderBody()}
      </Frame>
      {params.task !== null && (
        <CallTaskModal
          key={params.task}
          taskId={params.task}
          myUserId={myUserId}
          onClose={closeTask}
          onChanged={changed}
          onCompleted={completed}
        />
      )}
    </div>
  );
};

export default CallTasksTab;
